import { Controller } from "stimulus"
import ga from "../utils/ga"
import { Consent, userHasOptedOutOfCookiesForCategory } from "../utils/consent"
import { getExperimentCookie, setExperimentCookie } from "../utils/cookies"
import experiments from "../stores/experiment_toggle_store"
import reduce from "lodash/reduce"
import isEmpty from "lodash/isEmpty"
import forOwn from "lodash/forOwn"
import flatten from "lodash/flatten"
import sample from "lodash/sample"
import times from "lodash/times"
import includes from "lodash/includes"

export default class extends Controller {
  static targets = ["variant", "cover"]
  static experimentsConfig

  initialize() {
    if (!this.userRevokedExperimentsConsent()) {
      this.experimentsConfig = JSON.parse(document.body.dataset.experiments)
      this.runExperiments()
    }
    // always remove covers so the default can be seen
    // even when no experiment permission is given
    this.removeCovers()
  }

  userRevokedExperimentsConsent() {
    return userHasOptedOutOfCookiesForCategory(Consent.preferences)
  }

  independentExperiments() {
    return Object.values(this.experimentsConfig).filter((e) => isEmpty(e.requires))
  }

  dependentExperiments() {
    const controller = this
    return Object.values(this.experimentsConfig).filter(
      (e) => !isEmpty(e.requires) && controller.requirementsMet(this.experimentsConfig, e.requires)
    )
  }

  getExperimentTogglesFromUrlParams() {
    // ?experiment_toggles=activeToggle,!inactiveToggle
    const queryString = new URL(document.location.toString()).searchParams.get("experiment_toggles")
    if (!queryString) return

    const toggles = queryString.split(",")
    toggles.forEach((toggle) => {
      const toggleValue = this.toggleToValue(toggle)
      experiments.merge({ [toggleValue[0]]: toggleValue[1] })
    })
  }

  // turn "!toggleName" into ["toggleName", false] and "toggleName" into ["toggleName", true]
  toggleToValue(toggle) {
    if (toggle.charAt(0) === "!") {
      return [toggle.substring(1), false]
    } else {
      return [toggle, true]
    }
  }

  getExperimentTogglesFromEnrolments(enrolments) {
    Object.keys(enrolments).forEach((expId) => {
      const experimentToggles = this.findExperimentToggles(expId, enrolments[expId])
      experiments.merge(experimentToggles)
    })
  }

  findExperimentToggles(id, variant) {
    const configName = Object.keys(this.experimentsConfig).find((key) => this.experimentsConfig[key].id === id)
    const config = this.experimentsConfig[configName]
    return config?.variants?.[variant]
  }

  runExperiments() {
    const independentExperiments = this.independentExperiments()
    let independentEnrolments = {}

    if (!isEmpty(independentExperiments)) {
      independentEnrolments = this.setCookies(independentExperiments)
    }

    const dependentExperiments = this.dependentExperiments()
    let dependentEnrolments = {}

    if (!isEmpty(dependentExperiments)) {
      dependentEnrolments = this.setCookies(dependentExperiments)
    }

    const enrolments = { ...independentEnrolments, ...dependentEnrolments }

    this.getExperimentTogglesFromEnrolments(enrolments)
    this.getExperimentTogglesFromUrlParams()

    this.showSelectedVariants()

    this.reportGA(enrolments)
  }

  showSelectedVariants(selectedExperimentToggles) {
    this.variantTargets.forEach((domElement) => {
      this.toggleDomElementVisibility(domElement)
    })
  }

  removeCovers() {
    this.coverTargets.forEach((e) => e.remove())
  }

  shouldBeVisible(domElement) {
    return domElement.dataset.experimentToggle.split("&").every((toggle) => {
      const toggleValue = this.toggleToValue(toggle)

      return experiments.on(toggleValue[0]) === toggleValue[1]
    })
  }

  toggleDomElementVisibility(domElement) {
    if (this.shouldBeVisible(domElement)) {
      domElement.classList.remove("experiment-variant--hidden")
    } else {
      domElement.remove()
    }
  }

  requirementsMet(experiments, requiredExperiments) {
    return reduce(
      requiredExperiments,
      function (memo, variants, name) {
        const requiredExperiment = experiments[name]

        if (!requiredExperiment) {
          return false
        }

        const requiredVariant = getExperimentCookie(`market_experiment_${requiredExperiment.id}`)

        return memo && includes(variants.variants, requiredVariant)
      },
      true
    )
  }

  selectVariant(experiment) {
    const weightedVariants = {}

    Object.keys(experiment.variants).forEach((variant, i) => {
      let weight = 1

      if (
        typeof experiment.variantsWeights !== "undefined" &&
        experiment.variantsWeights &&
        experiment.variantsWeights.length >= i
      ) {
        weight = experiment.variantsWeights[i]
      }

      weightedVariants[variant] = weight
    })

    const variants = []

    forOwn(weightedVariants, function (weight, variant) {
      variants.push(times(weight, () => variant))
    })

    return sample(flatten(variants))
  }

  setCookies(experiments) {
    const controller = this

    const enrolments = reduce(
      experiments,
      (memo, experiment) => {
        const cookieName = `market_experiment_${experiment.id}`
        const cookie = getExperimentCookie(cookieName)

        if (!cookie) {
          const variant = controller.selectVariant(experiment)
          setExperimentCookie(cookieName, variant)
        }

        memo[experiment.id] = getExperimentCookie(cookieName)

        return memo
      },
      {}
    )

    return enrolments
  }

  reportGA(enrolments) {
    const enrolmentsString = Object.entries(enrolments)
      .map(([key, value]) => `${key}.${value}`)
      .join("!")

    if (isEmpty(enrolmentsString)) {
      return
    }

    if (!window.gaEnrolments) {
      window.gaEnrolments = []
    }

    this.setGaDimension("exp", enrolmentsString)
    this.setGaDimension("dimension21", enrolmentsString)
    this.setGaDimension("dimension22", enrolmentsString)
  }

  setGaDimension(dimension, enrolmentsString) {
    const enrolmentKey = [dimension, enrolmentsString].join("-")
    window.gaEnrolments ||= []

    if (!window.gaEnrolments.includes(enrolmentKey)) {
      ga("set", dimension, enrolmentsString)
      window.gaEnrolments.push(enrolmentKey)
    }
  }
}
